<template>
  <div>
    <DataprocessingConsentDialog
      v-if="isOpen && !$store.state.isDataprocessingConsentAccepted"
    >
    </DataprocessingConsentDialog>
    <v-btn
      v-if="!isIframeOptionEnabled"
      fab
      dark
      large
      color="primary"
      class="sc-launcher"
      :class="{ opened: isOpen }"
      :aria-label="$t('new_messages')"
      @click.prevent="isOpen ? close() : openAndFocus()"
    >
      <div v-if="newMessagesCount > 0 && !isOpen" class="sc-new-messages-count">
        {{ newMessagesCount }}
      </div>
      <v-icon v-if="isOpen"> mdi-close </v-icon>
      <v-icon v-else>
        mdi-message-reply-text
      </v-icon>
    </v-btn>

    <ChatWindow
      v-if="isConversationStarted"
      :participants="participants"
      :title="chatWindowTitle"
      :title-image-url="titleImageUrl"
      :is-open="isOpen"
      :on-close="close"
      :on-new="restartChat"
      :on-back="goBack"
      :on-restart="restartMessenger"
      :show-file="showFile"
      :show-typing-indicator="showTypingIndicator"
      :colors="colors"
      :always-scroll-to-bottom="alwaysScrollToBottom"
      :message-styling="messageStyling"
      :disable-user-list-toggle="disableUserListToggle"
      @scrollToTop="$emit('scrollToTop')"
      @onType="$emit('onType')"
      @edit="$emit('edit', $event)"
      @remove="$emit('remove', $event)"
    >
      <template v-slot:header>
        <slot name="header"></slot>
      </template>
      <template v-slot:user-avatar="scopedProps">
        <slot
          name="user-avatar"
          :user="scopedProps.user"
          :message="scopedProps.message"
        >
        </slot>
      </template>
      <template v-slot:text-message-body="scopedProps">
        <slot
          name="text-message-body"
          :message="scopedProps.message"
          :messageText="scopedProps.messageText"
          :messageColors="scopedProps.messageColors"
          :me="scopedProps.me"
        >
        </slot>
      </template>
      <template v-slot:system-message-body="scopedProps">
        <slot name="system-message-body" :message="scopedProps.message"></slot>
      </template>
      <template v-slot:text-message-toolbox="scopedProps">
        <slot
          name="text-message-toolbox"
          :message="scopedProps.message"
          :me="scopedProps.me"
        >
        </slot>
      </template>
    </ChatWindow>
  </div>
</template>
<script>
const ChatWindow = () => import('./ChatWindow.vue')
const DataprocessingConsentDialog = () =>
  import('./DataprocessingConsentDialog.vue')

import CloseIcon from '../assets/close-icon.png'
import OpenIcon from '../assets/logo-no-bg.svg'
import { enableIframeOption } from '@/app.config'

export default {
  name: 'Launcher',
  components: {
    ChatWindow,
    DataprocessingConsentDialog
  },
  props: {
    icons: {
      type: Object,
      required: false,
      default: function() {
        return {
          open: {
            img: OpenIcon,
            name: 'default'
          },
          close: {
            img: CloseIcon,
            name: 'default'
          }
        }
      }
    },
    isOpen: {
      type: Boolean,
      required: true
    },
    isConversationStarted: {
      type: Boolean,
      required: true
    },
    open: {
      type: Function,
      required: true
    },
    close: {
      type: Function,
      required: true
    },
    restartChat: {
      type: Function,
      required: true
    },
    goBack: {
      type: Function,
      required: true
    },
    restartMessenger: {
      type: Function,
      required: true
    },
    showFile: {
      type: Boolean,
      default: false
    },
    participants: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: () => ''
    },
    titleImageUrl: {
      type: String,
      default: () => ''
    },
    newMessagesCount: {
      type: Number,
      default: () => 0
    },
    showTypingIndicator: {
      type: String,
      default: () => ''
    },
    colors: {
      type: Object,
      required: false,
      validator: c =>
        'header' in c &&
        'bg' in c.header &&
        'text' in c.header &&
        'launcher' in c &&
        'bg' in c.launcher &&
        'messageList' in c &&
        'bg' in c.messageList &&
        'sentMessage' in c &&
        'bg' in c.sentMessage &&
        'text' in c.sentMessage &&
        'receivedMessage' in c &&
        'bg' in c.receivedMessage &&
        'text' in c.receivedMessage &&
        'userInput' in c &&
        'bg' in c.userInput &&
        'text' in c.userInput,
      default: function() {
        return {
          header: {
            bg: '#4e8cff',
            text: '#ffffff'
          },
          launcher: {
            bg: '#4e8cff'
          },
          messageList: {
            bg: '#ffffff'
          },
          sentMessage: {
            bg: '#4e8cff',
            text: '#ffffff'
          },
          receivedMessage: {
            bg: '#f4f7f9',
            text: '#ffffff'
          },
          userInput: {
            bg: '#f4f7f9',
            text: '#565867'
          }
        }
      }
    },
    alwaysScrollToBottom: {
      type: Boolean,
      default: () => false
    },
    messageStyling: {
      type: Boolean,
      default: () => false
    },
    disableUserListToggle: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialog: false
    }
  },
  computed: {
    isIframeOptionEnabled() {
      return enableIframeOption === '1'
    },
    chatWindowTitle() {
      if (this.$store.state.headerText) return this.$store.state.headerText

      if (this.$vuetify.theme.themes.name == 'langimmobilien') {
        return 'Lang Immobilien'
      }
      if (this.$vuetify.theme.themes.name == 'phi') {
        return ''
      }
      if (this.$vuetify.theme.themes.name == 'ivv') {
        return 'IVV'
      }
      if (this.title !== '') {
        return this.title
      }

      if (this.participants.length === 0) {
        return 'You'
      } else if (this.participants.length > 1) {
        return 'You, ' + this.participants[0].name + ' & others'
      } else {
        return 'You & ' + this.participants[0].name
      }
    }
  },
  methods: {
    openAndFocus() {
      this.open()
      this.$root.$emit('focusUserInput')
    }
  }
}
</script>
<style scoped>
.sc-launcher {
  position: fixed;
  right: 25px;
  bottom: 25px;
  border-radius: 50%;
}

.sc-new-messages-count {
  position: absolute;
  top: -3px;
  left: 41px;
  display: flex;
  justify-content: center;
  flex-direction: column;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background: #ff4646;
  color: white;
  text-align: center;
  margin: auto;
  font-size: 12px;
  font-weight: 500;
}
</style>
